import React, { useState, useEffect } from 'react';
import { X, Eye, Info, Building2, Users, UserCheck, Euro, ArrowLeftRight, MessageSquare, Calendar, TrendingDown, History, AlertCircle } from 'lucide-react';
import { Transaction } from '../../types/dashboard';
import { supabase } from '../../lib/supabase';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { getDistributionNegotiatorIds, parseDistributionKey } from '../../utils/retrocessions';

interface ViewTransactionModalProps {
  transaction: Transaction | null;
  isOpen: boolean;
  onClose: () => void;
}

export default function ViewTransactionModal({ transaction, isOpen, onClose }: ViewTransactionModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [netFeesWithoutTax, setNetFeesWithoutTax] = useState(0);
  const [retrocessions, setRetrocessions] = useState<any[]>([]);
  const [negotiatorRevenues, setNegotiatorRevenues] = useState<{
    id: string;
    name: string;
    role: string;
    percentage: number;
    revenue: number;
    retrocessions: {
      type: string;
      amount: number;
      distribution: string;
    }[];
  }[]>([]);
  const [coproDetails, setCoproDetails] = useState<{ numero: string; residence: string } | null>(null);
  const [coproNewcoDetails, setCoproNewcoDetails] = useState<{ numero: string; residence: string } | null>(null);
  const [coproSyndicDetails, setCoproSyndicDetails] = useState<{ numero: string; residence: string } | null>(null);
  const [transactionDetails, setTransactionDetails] = useState<any>(null);
  const [regulHistory, setRegulHistory] = useState<any[]>([]);

  useEscapeKey(() => {
    if (isOpen) {
      onClose();
    }
  });

  useEffect(() => {
    if (transaction?.id) {
      loadTransactionDetails();
    }
  }, [transaction]);

  const loadTransactionDetails = async () => {
    if (!transaction?.id) return;

    try {
      setLoading(true);
      console.log('Loading transaction details for ID:', transaction.id);

      // Fetch complete transaction details directly from Supabase
      const { data: transactionData, error: transactionError } = await supabase
        .from('transactions')
        .select(`
          *,
          transaction_agencies (
            agency
          ),
          transaction_negotiators (
            negotiator:negotiators (
              id,
              firstname,
              lastname
            ),
            role,
            percentage,
            agency
          ),
          transaction_assistants (
            assistant:assistants (
              id,
              firstname,
              lastname
            ),
            role
          ),
          transaction_retrocessions (
            *,
            primary_negotiator:negotiators!primary_negotiator_id(id, firstname, lastname),
            secondary_negotiator:negotiators!secondary_negotiator_id(id, firstname, lastname),
            tertiary_negotiator:negotiators!tertiary_negotiator_id(id, firstname, lastname),
            quaternary_negotiator:negotiators!quaternary_negotiator_id(id, firstname, lastname)
          )
        `)
        .eq('id', transaction.id)
        .single();

      if (transactionError) {
        console.error('Error fetching transaction details:', transactionError);
        throw transactionError;
      }
      
      if (!transactionData) {
        console.error('No transaction data found');
        throw new Error('Transaction not found');
      }

      console.log('Transaction data from Supabase:', transactionData);
      setTransactionDetails(transactionData);

      // Load copro details based on property_origin
      if (transactionData.property_origin === 'lefeuvre' && transactionData.copro_id) {
        const { data: coproData, error: coproError } = await supabase
          .from('copros')
          .select('numero, residence')
          .eq('id', transactionData.copro_id)
          .single();

        if (!coproError && coproData) {
          setCoproDetails(coproData);
        }
      } else {
        setCoproDetails(null);
      }

      if (transactionData.property_origin === 'newco' && transactionData.copro_newco_id) {
        const { data: coproData, error: coproError } = await supabase
          .from('copros_newco')
          .select('numero, residence')
          .eq('id', transactionData.copro_newco_id)
          .single();

        if (!coproError && coproData) {
          setCoproNewcoDetails(coproData);
        }
      } else {
        setCoproNewcoDetails(null);
      }

      if (transactionData.property_origin === 'syndic' && transactionData.copro_syndic_id) {
        const { data: coproData, error: coproError } = await supabase
          .from('copros_syndic')
          .select('numero, residence')
          .eq('id', transactionData.copro_syndic_id)
          .single();

        if (!coproError && coproData) {
          setCoproSyndicDetails(coproData);
        }
      } else {
        setCoproSyndicDetails(null);
      }

      // Load regularization history
      const { data: regulData, error: regulError } = await supabase
        .from('regul_requests')
        .select(`
          *,
          user:requested_by (
            firstname,
            lastname
          ),
          reviewer:reviewed_by (
            firstname,
            lastname
          )
        `)
        .eq('transaction_id', transaction.id)
        .order('created_at', { ascending: false });

      if (regulError) {
        console.error('Error loading regul history:', regulError);
      } else {
        setRegulHistory(regulData || []);
      }

      // Ensure transaction_retrocessions is an array
      const transactionRetrocessions = transactionData.transaction_retrocessions ?? [];

      // For each retrocession with custom distribution, load negotiators
      const enrichedRetrocessions = await Promise.all(transactionRetrocessions.map(async (retrocession: any) => {
        if (retrocession.distribution_percentages) {
          const negotiatorIds = getDistributionNegotiatorIds(retrocession.distribution_percentages);

          const { data: negotiatorsData } = await supabase
            .from('negotiators')
            .select('id, firstname, lastname')
            .in('id', negotiatorIds);

          const negotiatorsMap = (negotiatorsData || []).reduce((acc: any, n: any) => {
            acc[n.id] = n;
            return acc;
          }, {});

          return {
            ...retrocession,
            negotiators_map: negotiatorsMap
          };
        }
        return retrocession;
      }));

      setRetrocessions(enrichedRetrocessions);

      // Calculate total retrocessions
      const totalRetrocessions = enrichedRetrocessions.reduce((sum, r) => sum + r.amount, 0);
      const netFees = transactionData.fees_without_tax - totalRetrocessions;
      setNetFeesWithoutTax(netFees);

      // Initialize revenues with base percentages
      // Ensure transaction_negotiators is an array
      const baseRevenues = (transactionData.transaction_negotiators ?? []).map((negotiator: any) => ({
        id: negotiator.negotiator.id,
        agency: negotiator.agency || null,
        name: `${negotiator.negotiator.firstname} ${negotiator.negotiator.lastname}`,
        role: negotiator.role,
        percentage: negotiator.percentage,
        revenue: (transactionData.fees_without_tax * negotiator.percentage) / 100,
        retrocessions: [] as { type: string; amount: number; distribution: string }[]
      }));

      // Adjust revenues based on retrocessions
      enrichedRetrocessions.forEach((retrocession: any) => {
        switch (retrocession.distribution_type) {
          case 'single':
            // 100% to primary negotiator
            const singleNegotiator = baseRevenues.find(n => n.id === retrocession.primary_negotiator_id);
            if (singleNegotiator) {
              singleNegotiator.revenue -= retrocession.amount;
              singleNegotiator.retrocessions.push({
                type: retrocession.type,
                amount: retrocession.amount,
                distribution: '100%'
              });
            }
            break;

          case 'equal':
          case 'custom':
            // Use distribution_percentages
            if (retrocession.distribution_percentages) {
              const consumed = new Set<number>();
              type RevenueLine = (typeof baseRevenues)[number];
              Object.entries(retrocession.distribution_percentages).forEach(([key, percentage]) => {
                if (key !== 'total') {
                  // La clé vaut "id" (ancien format) ou "id|AGENCE" : on cible la
                  // bonne ligne, un négociateur pouvant figurer dans deux agences
                  const { negotiatorId, agency } = parseDistributionKey(key.split('#')[0]);
                  let matchIndex = baseRevenues.findIndex(
                    (n: RevenueLine, i: number) => n.id === negotiatorId && n.agency === agency && !consumed.has(i)
                  );
                  if (matchIndex === -1) {
                    matchIndex = baseRevenues.findIndex(
                      (n: RevenueLine, i: number) => n.id === negotiatorId && !consumed.has(i)
                    );
                  }
                  const negotiator = matchIndex === -1 ? undefined : baseRevenues[matchIndex];
                  if (negotiator) {
                    consumed.add(matchIndex);
                    const amount = (retrocession.amount * (percentage as number)) / 100;
                    negotiator.revenue -= amount;
                    negotiator.retrocessions.push({
                      type: retrocession.type,
                      amount,
                      distribution: `${percentage}%`
                    });
                  }
                }
              });
            }
            break;
        }
      });

      setNegotiatorRevenues(baseRevenues);

    } catch (err) {
      console.error('Error loading transaction details:', err);
      setError('Erreur lors du chargement des détails');
    } finally {
      setLoading(false);
    }
  };

  const getDistributionLabel = (retrocession: any) => {
    const getNegotiatorName = (negotiator: any) => 
      negotiator ? `${negotiator.firstname} ${negotiator.lastname}` : '';

    if (retrocession.distribution_percentages) {
      const lines = [];
      Object.entries(retrocession.distribution_percentages).forEach(([key, value]) => {
        if (key !== 'total') {
          // La clé vaut "id" (ancien format) ou "id|AGENCE"
          const { negotiatorId: id, agency } = parseDistributionKey(key.split('#')[0]);
          let negotiatorName = '';
          
          // Search for negotiator in all possible columns
          if (retrocession.negotiators_map?.[id]) {
            const negotiator = retrocession.negotiators_map[id];
            negotiatorName = `${negotiator.firstname} ${negotiator.lastname}`;
          } else if (retrocession.primary_negotiator?.id === id) {
            negotiatorName = getNegotiatorName(retrocession.primary_negotiator);
          } else if (retrocession.secondary_negotiator?.id === id) {
            negotiatorName = getNegotiatorName(retrocession.secondary_negotiator);
          } else if (retrocession.tertiary_negotiator?.id === id) {
            negotiatorName = getNegotiatorName(retrocession.tertiary_negotiator);
          } else if (retrocession.quaternary_negotiator?.id === id) {
            negotiatorName = getNegotiatorName(retrocession.quaternary_negotiator);
          }

          if (negotiatorName) {
            lines.push(`${negotiatorName}${agency ? ` (${agency})` : ''} (${value}%)`);
          }
        }
      });
      return lines.join('\n');
    } else {
      switch (retrocession.distribution_type) {
        case 'single':
          return `100% ${getNegotiatorName(retrocession.primary_negotiator)}`;
        
        case 'equal': {
          const negotiators = [
            retrocession.primary_negotiator,
            retrocession.secondary_negotiator,
            retrocession.tertiary_negotiator,
            retrocession.quaternary_negotiator
          ].filter(Boolean);
          const percentage = Math.round(100 / negotiators.length);
          return negotiators.map(n => `${getNegotiatorName(n)} (${percentage}%)`).join('\n');
        }
        
        default:
          return 'Non spécifié';
      }
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (date: string | null | undefined) => {
    if (!date) return '-';
    try {
      return new Date(date).toLocaleDateString('fr-FR');
    } catch (e) {
      console.error('Error formatting date:', e, date);
      return '-';
    }
  };

  const getRetrocessionTypeLabel = (type: string) => {
    switch (type) {
      case 'champagne':
        return 'Champagne (21€)';
      case 'ambassadeur':
        return 'Ambassadeur (150€/200€/350€)';
      case 'diagnostic':
        return 'Diagnostic (Montant libre)';
      case 'recommandation_interne':
        return 'Recommandation interne (20% HT)';
      case 'uff':
        return 'UFF (20% HT)';
      case 'retrocession_lefeuvre':
        return 'Rétrocession Lefeuvre (20% HT)';
      case 'retrocession_newco':
        return 'Rétrocession Newco (20% HT)';
      case 'retrocession_syndic':
        return 'Rétrocession KS (20% HT)';
      case 'duo_coaching':
        return 'Duo Coaching (20% HT)';
      case 'intercabinet':
        return 'Intercabinet (Montant libre)';
      case 'geste_commercial':
        return 'Geste commercial (Montant libre)';
      case 'autre':
        return 'Autre (Montant libre)';
      default:
        return type;
    }
  };

  const getPropertyOriginLabel = (origin: string | undefined) => {
    switch (origin) {
      case 'gestion':
        return 'Bien de gestion KI';
      case 'lefeuvre':
        return 'Bien Lefeuvre';
      case 'newco':
        return 'Bien NewCo';
      case 'syndic':
        return 'Bien Kermarrec Syndic';
      default:
        return 'Non spécifié';
    }
  };

  const getLeadOriginLabel = (origin: string | undefined) => {
    switch (origin) {
      case 'acquereur':
        return 'Acquéreur';
      case 'prospection_boitage':
        return 'Prospection - boîtage';
      case 'prospection_porte_a_porte':
        return 'Prospection - porte à porte';
      case 'prospection_pige':
        return 'Prospection - pige';
      case 'prospection_preavis_gestion':
        return 'Prospection - préavis gestion';
      case 'prospection_rel_copro':
        return 'Prospection - rel copro';
      case 'prospection_panneau_picking':
        return 'Prospection - panneau (picking)';
      case 'prospection_capturama_ademe':
        return 'Prospection - capturama - ADEME';
      case 'prospection_ancien_client_signe':
        return 'Prospection - ancien client signé';
      case 'spontane_gestion_loc':
        return 'Spontané - gestion loc';
      case 'spontane_appel_agence':
        return 'Spontané - appel agence';
      case 'spontane_passage_agence':
        return 'Spontané - passage agence';
      case 'spontane_reseau_perso':
        return 'Spontané - réseau/perso';
      case 'spontane_syndic':
        return 'Spontané - syndic';
      case 'spontane_immobilier_entreprise':
        return 'Spontané - immobilier d\'entreprise';
      case 'spontane_temps_fort_campagne_publipostage':
        return 'Spontané temps fort - campagne publipostage';
      case 'spontane_interagence_transac':
        return 'Spontané - interagence transac';
      case 'relance_ancien_client_copro':
        return 'Relance ancien client copro';
      case 'spontane_mail_boite_transaction':
        return 'Spontané - mail boîte Transaction';
      case 'spontane_flyer':
        return 'Spontané - flyer';
      case 'relance_ancienne_estim_source_inconnue':
        return 'Relance ancienne estim (source inconnue)';
      default:
        return 'Non spécifié';
    }
  };

  if (!isOpen || !transaction) return null;

  // Use transactionDetails for display if available, otherwise fall back to transaction prop
  const displayData = transactionDetails || transaction;

  // Ensure agencies and negotiators arrays exist
  const agencies = displayData.transaction_agencies?.map((ta: any) => ta.agency) ?? [];
  const negotiators = displayData.transaction_negotiators?.map((tn: any) => ({
    name: `${tn.negotiator.firstname} ${tn.negotiator.lastname}`,
    role: tn.role,
    percentage: tn.percentage
  })) ?? [];

  // Debug information for dates
  console.log('Transaction dates:', {
    compromise_date: displayData.compromise_date,
    expected_acte_date: displayData.expected_acte_date,
    acte_date: displayData.acte_date,
    invoice_date: displayData.invoice_date,
    invoice_number: displayData.invoice_number,
    encaissement_date: displayData.encaissement_date,
    cancellation_date: displayData.cancellation_date,
    cancellation_reason: displayData.cancellation_reason
  });

  const STATUS_STYLES: Record<string, { bg: string; text: string; dot: string; label: string }> = {
    potentiel: { bg: 'bg-slate-100', text: 'text-slate-700', dot: 'bg-slate-400', label: 'Potentiel' },
    compromis: { bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-500', label: 'Compromis' },
    acte: { bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500', label: 'Acte' },
    encaisse: { bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500', label: 'Encaissé' },
    cloture: { bg: 'bg-indigo-100', text: 'text-indigo-700', dot: 'bg-indigo-500', label: 'Clôturé' },
    annule: { bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500', label: 'Annulé' },
  };
  const statusStyle = STATUS_STYLES[displayData.status] ?? STATUS_STYLES.potentiel;

  const Field = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div>
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">{label}</p>
      <p className="text-sm font-semibold text-slate-900">{value || '-'}</p>
    </div>
  );

  const SectionHeader = ({ icon: Icon, label, headerCls, iconCls, titleCls }: { icon: any; label: string; headerCls: string; iconCls: string; titleCls: string }) => (
    <div className={`px-5 py-3 border-b flex items-center gap-2.5 ${headerCls}`}>
      <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${iconCls}`}>
        <Icon size={14} />
      </div>
      <h3 className={`text-xs font-bold uppercase tracking-wider ${titleCls}`}>{label}</h3>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm flex items-end sm:items-center justify-center z-[200] p-0 sm:p-4">
      <div
        className="bg-white w-full sm:max-w-4xl sm:rounded-2xl flex flex-col overflow-hidden shadow-2xl"
        style={{ maxHeight: 'min(92vh, 100dvh)' }}
      >
        {/* ── Header ── */}
        <div className="flex-none bg-gradient-to-r from-slate-700 to-slate-900 px-6 py-5">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
                <Eye size={20} className="text-white" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-lg font-bold text-white leading-tight">
                    Transaction #{displayData.property_number}
                  </h2>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${statusStyle.bg} ${statusStyle.text}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${statusStyle.dot}`} />
                    {statusStyle.label}
                  </span>
                </div>
                <p className="text-slate-400 text-xs mt-0.5">Visualisation · lecture seule</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-xl bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors shrink-0"
            >
              <X size={18} className="text-white" />
            </button>
          </div>
        </div>

        {/* ── Scrollable content ── */}
        <div className="flex-1 overflow-y-auto bg-slate-50 p-5 space-y-4">

          {loading && (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent" />
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
              <AlertCircle size={16} className="shrink-0" />
              {error}
            </div>
          )}

          {/* Informations générales */}
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
            <SectionHeader icon={Info} label="Informations générales" headerCls="bg-indigo-50 border-indigo-100" iconCls="bg-indigo-100 text-indigo-600" titleCls="text-indigo-700" />
            <div className="p-5 grid grid-cols-2 gap-x-6 gap-y-4">
              <Field label="Type de mandat" value={displayData.mandate_type} />
              <Field label="Type de propriété" value={displayData.property_type} />
              <Field label="Vendeur" value={displayData.seller} />
              <Field label="Acquéreur" value={displayData.buyer} />
              <Field label="Origine du bien" value={getPropertyOriginLabel(displayData.property_origin)} />
              <Field label="Origine du lead" value={getLeadOriginLabel(displayData.lead_origin)} />
              <Field label="Date du mandat" value={formatDate(displayData.mandate_date)} />

              {displayData.property_origin === 'lefeuvre' && (
                <>
                  {coproDetails && <Field label="Copropriété Lefeuvre" value={`${coproDetails.numero} – ${coproDetails.residence}`} />}
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5">Type de bien Lefeuvre</p>
                    <div className="flex gap-2 flex-wrap">
                      {displayData.lefeuvre_syndic && <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">Syndic</span>}
                      {displayData.lefeuvre_gestion && <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">Gestion</span>}
                      {!displayData.lefeuvre_syndic && !displayData.lefeuvre_gestion && <span className="text-sm text-slate-400">Non spécifié</span>}
                    </div>
                  </div>
                </>
              )}
              {displayData.property_origin === 'newco' && (
                <>
                  {coproNewcoDetails && <Field label="Copropriété NewCo" value={`${coproNewcoDetails.numero} – ${coproNewcoDetails.residence}`} />}
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5">Type de bien NewCo</p>
                    <div className="flex gap-2 flex-wrap">
                      {displayData.newco_syndic && <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">Syndic</span>}
                      {displayData.newco_gestion && <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">Gestion</span>}
                      {!displayData.newco_syndic && !displayData.newco_gestion && <span className="text-sm text-slate-400">Non spécifié</span>}
                    </div>
                  </div>
                </>
              )}
              {displayData.property_origin === 'syndic' && (
                <>
                  {coproSyndicDetails && <Field label="Copropriété Kermarrec Syndic" value={`${coproSyndicDetails.numero} – ${coproSyndicDetails.residence}`} />}
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5">Type de bien Syndic</p>
                    <div className="flex gap-2 flex-wrap">
                      {displayData.syndic_syndic && <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">Syndic</span>}
                      {displayData.syndic_gestion && <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-violet-100 text-violet-700">Gestion</span>}
                      {!displayData.syndic_syndic && !displayData.syndic_gestion && <span className="text-sm text-slate-400">Non spécifié</span>}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Informations financières */}
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
            <SectionHeader icon={Euro} label="Informations financières" headerCls="bg-teal-50 border-teal-100" iconCls="bg-teal-100 text-teal-600" titleCls="text-teal-700" />
            <div className="p-5 grid grid-cols-3 gap-x-6 gap-y-4">
              <div className="bg-slate-50 rounded-xl p-4 text-center">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Prix de vente</p>
                <p className="text-lg font-bold text-slate-900">{formatCurrency(displayData.sale_price)}</p>
              </div>
              <div className="bg-teal-50 rounded-xl p-4 text-center">
                <p className="text-xs font-medium text-teal-600 uppercase tracking-wide mb-1">Honoraires TTC</p>
                <p className="text-lg font-bold text-teal-800">{formatCurrency(displayData.fees_with_tax)}</p>
              </div>
              <div className="bg-emerald-50 rounded-xl p-4 text-center">
                <p className="text-xs font-medium text-emerald-600 uppercase tracking-wide mb-1">Honoraires HT</p>
                <p className="text-lg font-bold text-emerald-800">{formatCurrency(displayData.fees_without_tax)}</p>
              </div>
            </div>
          </div>

          {/* Agences & Négociateurs */}
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
            <SectionHeader icon={Users} label="Agences et négociateurs" headerCls="bg-blue-50 border-blue-100" iconCls="bg-blue-100 text-blue-600" titleCls="text-blue-700" />
            <div className="p-5 grid grid-cols-2 gap-6">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Agences</p>
                <div className="space-y-1.5">
                  {agencies.map((agency: string, i: number) => (
                    <div key={i} className="flex items-center gap-2">
                      <Building2 size={13} className="text-slate-400 shrink-0" />
                      <span className="text-sm font-semibold text-slate-800">{agency}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Négociateurs</p>
                <div className="space-y-2">
                  {negotiators.map((n: any, i: number) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-slate-800">{n.name}</span>
                      <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">{n.role} · {n.percentage}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Assistantes */}
          {displayData.transaction_assistants && displayData.transaction_assistants.length > 0 && (
            <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
              <SectionHeader icon={UserCheck} label="Assistantes" headerCls="bg-amber-50 border-amber-100" iconCls="bg-amber-100 text-amber-600" titleCls="text-amber-700" />
              <div className="p-5 grid grid-cols-2 gap-4">
                {displayData.transaction_assistants.map((a: any, i: number) => (
                  <Field key={i} label={`Assistante ${a.role}`} value={`${a.assistant.firstname} ${a.assistant.lastname}`} />
                ))}
              </div>
            </div>
          )}

          {/* Dates */}
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
            <SectionHeader icon={Calendar} label="Dates" headerCls="bg-violet-50 border-violet-100" iconCls="bg-violet-100 text-violet-600" titleCls="text-violet-700" />
            <div className="p-5 grid grid-cols-2 gap-x-6 gap-y-4">
              <Field label="Compromis" value={formatDate(displayData.compromise_date)} />
              <Field label="Acte prévisionnel" value={formatDate(displayData.expected_acte_date)} />
              <Field label="Signature de l'acte" value={formatDate(displayData.acte_date)} />
              <Field label="Facture" value={formatDate(displayData.invoice_date)} />
              <Field label="N° de facture" value={displayData.invoice_number} />
              <Field label="Encaissement" value={formatDate(displayData.encaissement_date)} />
              {displayData.status === 'annule' && (
                <>
                  <Field label="Date d'annulation" value={formatDate(displayData.cancellation_date)} />
                  <Field label="Raison d'annulation" value={displayData.cancellation_reason} />
                </>
              )}
            </div>
          </div>

          {/* Rétrocessions */}
          {retrocessions.length > 0 && (
            <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
              <SectionHeader icon={ArrowLeftRight} label="Rétrocessions" headerCls="bg-rose-50 border-rose-100" iconCls="bg-rose-100 text-rose-600" titleCls="text-rose-700" />
              <div className="p-5 space-y-3">
                {retrocessions.map((r, i) => (
                  <div key={i} className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                    <div className="grid grid-cols-3 gap-4">
                      <Field label="Type" value={getRetrocessionTypeLabel(r.type)} />
                      <Field label="Montant" value={formatCurrency(r.amount)} />
                      <div>
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Répartition</p>
                        <p className="text-sm font-semibold text-slate-900 whitespace-pre-line">{getDistributionLabel(r)}</p>
                      </div>
                    </div>
                    {r.description && (
                      <p className="mt-3 text-sm text-slate-600 border-t border-slate-200 pt-3">{r.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Revenus négociateurs */}
          {negotiatorRevenues.length > 0 && (
            <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
              <SectionHeader icon={TrendingDown} label="Revenus des négociateurs" headerCls="bg-emerald-50 border-emerald-100" iconCls="bg-emerald-100 text-emerald-600" titleCls="text-emerald-700" />
              <div className="p-5 space-y-3">
                {negotiatorRevenues.map((n, i) => (
                  <div key={i} className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-bold text-slate-900">{n.name}</h4>
                      <span className="text-xs font-medium bg-slate-200 text-slate-600 px-2.5 py-1 rounded-full">{n.role} · {n.percentage}%</span>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white rounded-lg p-3 border border-slate-100">
                        <p className="text-xs text-slate-500 mb-0.5">Revenu brut</p>
                        <p className="font-bold text-slate-800">{formatCurrency((displayData.fees_without_tax * n.percentage) / 100)}</p>
                      </div>
                      <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-100">
                        <p className="text-xs text-emerald-600 mb-0.5">Revenu net</p>
                        <p className="font-bold text-emerald-800">{formatCurrency(n.revenue)}</p>
                      </div>
                    </div>
                    {n.retrocessions.length > 0 && (
                      <div className="mt-3 border-t border-slate-200 pt-3 space-y-1">
                        {n.retrocessions.map((retro, ri) => (
                          <p key={ri} className="text-xs text-slate-500">
                            <span className="font-medium text-rose-600">−{formatCurrency(retro.amount)}</span> · {getRetrocessionTypeLabel(retro.type)} ({retro.distribution})
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Commentaire */}
          {displayData.comments_transac && (
            <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
              <SectionHeader icon={MessageSquare} label="Commentaire" headerCls="bg-slate-50 border-slate-200" iconCls="bg-slate-200 text-slate-600" titleCls="text-slate-700" />
              <div className="p-5">
                <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{displayData.comments_transac}</p>
              </div>
            </div>
          )}

          {/* Historique des régularisations */}
          {regulHistory.length > 0 && (
            <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
              <SectionHeader icon={History} label="Historique des régularisations" headerCls="bg-orange-50 border-orange-100" iconCls="bg-orange-100 text-orange-600" titleCls="text-orange-700" />
              <div className="p-5 space-y-3">
                {regulHistory.map((r, i) => (
                  <div key={i} className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                    <div className="grid grid-cols-3 gap-4">
                      <Field label="Statut" value={r.status} />
                      <Field label="Demandé par" value={r.user ? `${r.user.firstname} ${r.user.lastname}` : undefined} />
                      <Field label="Date de demande" value={formatDate(r.created_at)} />
                    </div>
                    {r.request_comment && <p className="mt-3 text-sm text-slate-600 border-t border-slate-200 pt-3">{r.request_comment}</p>}
                    {r.admin_comment && <p className="mt-2 text-sm text-slate-600 italic">{r.admin_comment}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="flex-none border-t border-slate-200 bg-white px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-slate-700 to-slate-900 rounded-xl hover:from-slate-800 hover:to-black transition-all shadow-sm"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}